<template>
  <div>
    <section>
      <div class="field-row">
        <div class="field-column">
          <text-input
            :label="translations.email"
            type="email"
            :required="true"
            attribute="mail"
            :errorKeys="['mail', 'mail.0.value']"
          ></text-input>

          <text-input
            v-if="editing"
            type="password"
            :label="translations.currentPassword"
            :required="true"
            attribute="current_pass"
          ></text-input>

          <text-input
            type="password"
            :label="editing ? translations.newPassword : translations.password"
            :required="true"
            attribute="password"
          ></text-input>

          <text-input
            type="password"
            :label="editing ? translations.confirmNewPassword : translations.confirmPassword"
            :required="true"
            attribute="password_confirmation"
          ></text-input>

          <div class="in-thirds">
            <text-input
              :label="translations.firstName"
              :required="true"
              attribute="field_personal_first_name"
            ></text-input>

            <text-input
              :label="translations.middleName"
              :required="false"
              attribute="field_personal_middle_name"
            ></text-input>

            <text-input
              :label="translations.lastName"
              :required="true"
              attribute="field_personal_last_name"
            ></text-input>
          </div>

          <select-box
            :label="translations.formLabelRole"
            :required="true"
            :create_other="true"
            attribute="field_personal_school_role"
          >
            <select-option value="Director" v-text="translations.formValueRole1"></select-option>
            <select-option value="Principal/Head of School" v-text="translations.formValueRole2"></select-option>
            <select-option value="Other Type of Administrator" v-text="translations.formValueRole3"></select-option>
            <select-option value="Teacher" v-text="translations.formValueRole4"></select-option>
            <select-option
              value="School Counselor, psychologist or social worker"
              v-text="translations.formValueRole5"
            ></select-option>
            <select-option value="Not Applicable" v-text="translations.formValueRole6"></select-option>
          </select-box>

          <select-box :label="translations.formLabelAge" :required="true" attribute="field_personal_age">
            <select-option value="18-24 years old" v-text="translations.formValueAgeRange1"></select-option>
            <select-option value="25-34 years old" v-text="translations.formValueAgeRange2"></select-option>
            <select-option value="35-44 years old" v-text="translations.formValueAgeRange3"></select-option>
            <select-option value="45-54 years old" v-text="translations.formValueAgeRange4"></select-option>
            <select-option value="55-64 years old" v-text="translations.formValueAgeRange5"></select-option>
            <select-option value="65 years or older" v-text="translations.formValueAgeRange6"></select-option>
          </select-box>

          <select-box :label="translations.formLabelGender" :required="true" attribute="field_personal_gender">
            <select-option value="Female" v-text="translations.formValueGender1"></select-option>
            <select-option value="Male" v-text="translations.formValueGender2"></select-option>
            <select-option value="Non-binary/third gender" v-text="translations.formValueGender3"></select-option>
            <select-option value="other" v-text="translations.formValueGender4"></select-option>
            <select-option value="Prefer not to say" v-text="translations.formValueGender5"></select-option>
          </select-box>

          <text-input
            :hidden="profile.field_personal_gender !== 'other'"
            :label="translations.formLabelOtherGender"
            attribute="field_personal_gender_other"
          ></text-input>

          <select-box :label="translations.formLabelCountry" :required="true" attribute="field_school_address_country">
            <select-option
              v-for="{ isoCode, name } in country_options"
              :value="isoCode"
              v-text="name"
              :key="isoCode"
            ></select-option>
          </select-box>

          <select-box
            :label="translations.formLabelRace"
            :required="true"
            :multiple="true"
            :hidden="profile.field_school_address_country !== 'US'"
            attribute="field_personal_ethnicity_race"
          >
            <select-option
              value="Hispanic or Latin or Spanish Origin of any race"
              v-text="translations.formValueRace1"
            ></select-option>
            <select-option
              value="American Indian or Alaskan Native"
              v-text="translations.formValueRace2"
            ></select-option>
            <select-option value="Asian" v-text="translations.formValueRace3"></select-option>
            <select-option
              value="Native Hawaiian or other pacific Islander"
              v-text="translations.formValueRace4"
            ></select-option>
            <select-option value="Black or African American" v-text="translations.formValueRace5"></select-option>
            <select-option value="White" v-text="translations.formValueRace6"></select-option>
            <select-option value="Race unknown" v-text="translations.formValueRace7"></select-option>
            <select-option value="Prefer not to say" v-text="translations.formValueRace8"></select-option>
          </select-box>
        </div>

        <div class="field-column">
          <text-area :label="translations.formLabelReferral" attribute="field_other_referral"></text-area>

          <select-box
            :label="translations.formLabelEducation"
            :required="true"
            attribute="field_personal_education_level"
          >
            <select-option value="none" v-text="translations.formValueEducation1"></select-option>
            <select-option value="some elementary" v-text="translations.formValueEducation2"></select-option>
            <select-option value="some high school" v-text="translations.formValueEducation3"></select-option>
            <select-option value="high school" v-text="translations.formValueEducation4"></select-option>
            <select-option value="some college" v-text="translations.formValueEducation5"></select-option>
            <select-option value="vocational training" v-text="translations.formValueEducation6"></select-option>
            <select-option value="Associate degree" v-text="translations.formValueEducation7"></select-option>
            <select-option value="Bachelor's degree" v-text="translations.formValueEducation8"></select-option>
            <select-option value="Master's degree" v-text="translations.formValueEducation9"></select-option>
            <select-option value="Professional degree" v-text="translations.formValueEducation10"></select-option>
            <select-option value="Doctorate degree" v-text="translations.formValueEducation11"></select-option>
          </select-box>

          <select-box
            :label="translations.formLabelStudentGrades"
            :required="true"
            :create_other="true"
            :multiple="true"
            attribute="field_personal_student_age"
          >
            <select-option value="Pre-school" v-text="translations.formValueStudentGrades1"></select-option>
            <select-option
              value="Kindergarten or equivalent"
              v-text="translations.formValueStudentGrades2"
            ></select-option>
            <select-option value="Grade 1" v-text="translations.formValueStudentGrades3"></select-option>
            <select-option value="Grade 2" v-text="translations.formValueStudentGrades4"></select-option>
            <select-option value="Grade 3" v-text="translations.formValueStudentGrades5"></select-option>
            <select-option value="Grade 4" v-text="translations.formValueStudentGrades6"></select-option>
            <select-option value="Grade 5" v-text="translations.formValueStudentGrades7"></select-option>
            <select-option value="Grade 6" v-text="translations.formValueStudentGrades8"></select-option>
            <select-option value="Grade 7" v-text="translations.formValueStudentGrades9"></select-option>
            <select-option value="Grade 8" v-text="translations.formValueStudentGrades10"></select-option>
            <select-option value="High School" v-text="translations.formValueStudentGrades11"></select-option>
            <select-option value="District-wide" v-text="translations.formValueStudentGrades12"></select-option>
            <select-option value="Not Applicable" v-text="translations.formValueStudentGrades13"></select-option>
          </select-box>

          <select-box
            :label="translations.formLabelStudentAges"
            :required="true"
            :limit="3"
            :multiple="true"
            attribute="field_personal_student_age_exact"
          >
            <select-option value="&lt; 5" v-text="translations.formValueStudentAges1"></select-option>
            <select-option value="5 - 7" v-text="translations.formValueStudentAges2"></select-option>
            <select-option value="8 - 11" v-text="translations.formValueStudentAges3"></select-option>
            <select-option value="12 - 14" v-text="translations.formValueStudentAges4"></select-option>
            <select-option value="15 - 17" v-text="translations.formValueStudentAges5"></select-option>
            <select-option value="&gt; 17" v-text="translations.formValueStudentAges6"></select-option>
            <select-option value="All Ages" v-text="translations.formValueStudentAges7"></select-option>
          </select-box>

          <select-box
            :label="translations.formLabelYearsExperience"
            :required="true"
            attribute="field_personal_years_experience"
          >
            <select-option value="0-2 years" v-text="translations.formValueYearsExperience1"></select-option>
            <select-option value="3-5 years" v-text="translations.formValueYearsExperience2"></select-option>
            <select-option value="6-10 years" v-text="translations.formValueYearsExperience3"></select-option>
            <select-option value="11-20 years" v-text="translations.formValueYearsExperience4"></select-option>
            <select-option value="21-30 years" v-text="translations.formValueYearsExperience5"></select-option>
            <select-option value="30+ years" v-text="translations.formValueYearsExperience6"></select-option>
            <select-option value="Not Applicable" v-text="translations.formValueYearsExperience7"></select-option>
          </select-box>

          <text-area
            :label="translations.formLabelAffiliation"
            :required="true"
            attribute="field_personal_affiliation"
          ></text-area>

          <select-box
            :label="translations.formLabelSchoolType"
            :required="true"
            :limit="3"
            :multiple="true"
            attribute="field_personal_school_type"
          >
            <select-option value="Public School" v-text="translations.formValueSchoolType1"></select-option>
            <select-option value="Private School" v-text="translations.formValueSchoolType2"></select-option>
            <select-option value="Charter School" v-text="translations.formValueSchoolType3"></select-option>
            <select-option value="Independent School" v-text="translations.formValueSchoolType4"></select-option>
            <select-option value="Religious School" v-text="translations.formValueSchoolType5"></select-option>
            <select-option value="After School program" v-text="translations.formValueSchoolType6"></select-option>
            <select-option value="Summer/Vacation program" v-text="translations.formValueSchoolType7"></select-option>
            <select-option value="Governmental organization" v-text="translations.formValueSchoolType8"></select-option>
            <select-option
              value="Non-governmental organization"
              v-text="translations.formValueSchoolType9"
            ></select-option>
            <select-option
              value="Social service organization"
              v-text="translations.formValueSchoolType10"
            ></select-option>
            <select-option value="College/University" v-text="translations.formValueSchoolType11"></select-option>
            <select-option value="other" v-text="translations.formValueSchoolType12"></select-option>
            <select-option value="Not Applicable" v-text="translations.formValueSchoolType13"></select-option>
          </select-box>

          <text-area
            :label="translations.formLabelSchoolTypeOther"
            attribute="field_personal_school_type_other"
            :hidden="!profile.field_personal_school_type.includes('other')"
          ></text-area>
        </div>
      </div>
    </section>

    <!-- school info -->
    <section>
      <h2 v-text="translations.formSectionSchoolDetails"></h2>

      <div class="in-half">
        <text-input :label="translations.formLabelSchoolAddress1" attribute="field_school_address_line_1"></text-input>
        <text-input :label="translations.formLabelSchoolAddress2" attribute="field_school_address_line_2"></text-input>
      </div>

      <div class="in-thirds">
        <text-input :label="translations.formLabelSchoolCity" attribute="field_school_address_city"></text-input>
        <text-input :label="translations.formLabelSchoolState" attribute="field_school_address_state"></text-input>
        <text-input
          :label="translations.formLabelSchoolDistrict"
          attribute="field_school_address_district"
        ></text-input>
      </div>

      <phone-input :label="translations.formLabelSchoolPhone" attribute="field_school_contact_landline"></phone-input>

      <phone-input :label="translations.formLabelSchoolContact" attribute="field_school_contact_mobile"></phone-input>

      <text-input
        :label="translations.formLabelSchoolEmail"
        type="email"
        attribute="field_school_contact_email"
      ></text-input>
    </section>

    <!-- past experience -->
    <section>
      <h2 v-text="translations.formSectionPastExperience"></h2>

      <radio-input
        toggles="field_exp_programs"
        attribute="field_exp_training"
        :label="translations.formLabelExpTraining"
      >
        <radio-option :value="true" :label="translations.formValueExpTraining1"></radio-option>
        <radio-option :value="false" :label="translations.formValueExpTraining2"></radio-option>
      </radio-input>

      <text-area :label="translations.formLabelExpPrograms" attribute="field_exp_programs"></text-area>
    </section>
  </div>
</template>

<script>
// We purposely import by the Node.js entrypoint here to gain access to all locales.
import countries from "i18n-iso-countries/entry-node";
import { get, map } from "lodash";
import { mapState } from "vuex";
import TextArea from "./TextArea";
import TextInput from "./TextInput";
import RadioInput from "./RadioInput";
import RadioOption from "./RadioOption";
import SelectBox from "./SelectBox";
import SelectOption from "./SelectOption";
import PhoneInput from "./PhoneInput";
import HasTranslatedText from "../../mixins/HasTranslatedText";

export default {
  mixins: [HasTranslatedText],

  props: {
    editing: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },

  computed: {
    ...mapState({
      profile: state => state.registration.profile,
      errors: state => state.registration.errors,
    }),

    country_options() {
      const LANG_CODE = get(window, "vueBridge.langCode", "en");
      const countryNames = countries.getNames(LANG_CODE, { select: "official" });
      return map(countryNames, (value, key) => {
        return { isoCode: key, name: value };
      });
    },
  },

  components: {
    TextArea,
    TextInput,
    RadioInput,
    RadioOption,
    SelectBox,
    SelectOption,
    PhoneInput,
  },
};
</script>
